import {
  SET_PROJECTS,
  SET_PROJECT_DETAILS,
  SET_CURRENT_PAGE,
  SET_CURRENT_PROJECT,
  SET_FILTERS,
  SET_ACTIVE_FILTER,
  TOGGLE_LOADING
} from '@/store/mutation-types'
import service from '@/service'
import isEmpty from 'lodash/isEmpty'
import nbsp from '@/assets/js/nbsp'

const PROJECTS_PER_PAGE = 10

const state = {
  items: [],
  currentProject: -1,
  currentPage: 0,
  filters: [],
  activeFilter: -1,
  details: [],
  loading: []
}

const getters = {
  filteredProjects (state, getters, { i18n }) {
    const localizeProject = project => ({
      ...project,
      title: project.title[i18n.locale],
      slug: project.slug[i18n.locale]
    })

    return state.items
      .map(localizeProject)
      .filter(item => (
        item.categories.includes(state.activeFilter) || state.activeFilter === -1
      ))
  },
  projects (state, getters) {
    const startIndex = state.currentPage * PROJECTS_PER_PAGE

    return getters.filteredProjects.slice(startIndex, startIndex + PROJECTS_PER_PAGE)
  },
  pageCount (state, getters) {
    return Math.ceil(getters.filteredProjects.length / PROJECTS_PER_PAGE)
  },
  currentProjectIndex (state, getters) {
    const currentProject = getters.filteredProjects.find(project => (
      project.id === state.currentProject
    ))

    return getters.filteredProjects.indexOf(currentProject)
  },
  prevPage (state) {
    return !!state.currentPage && state.currentPage - 1
  },
  nextPage (state, getters) {
    return state.currentPage + 1 !== getters.pageCount && state.currentPage + 1
  },
  prevProject (state, getters) {
    const prev = getters.filteredProjects[getters.currentProjectIndex - 1]
    return !!getters.currentProjectIndex && prev
  },
  nextProject (state, getters) {
    const isNotLast = getters.currentProjectIndex + 1 !== getters.filteredProjects.length
    const next = getters.filteredProjects[getters.currentProjectIndex + 1]
    return isNotLast && next
  },
  filters (state, getters, { i18n }) {
    return state.filters.map(filter => (
      {
        id: filter.id,
        title: filter[i18n.locale],
        isActive: filter.id === state.activeFilter
      }
    ))
  },
  primaryFilter (state, getters) {
    return getters.filters.find(filter => filter.showInMenu) || getters.filters[0] || {}
  },
  isLoading (state) {
    return loadingName => state.loading.includes(loadingName)
  },
  details (state, getters, { i18n }) {
    const currentDetails = state.details.find(entry => entry.id === state.currentProject)

    const localizeWidget = widget => ({
      ...widget,
      title: widget.title[i18n.locale],
      description: widget.description[i18n.locale],
      photoDesc: widget.photoDesc[i18n.locale]

    })

    const localizeDetails = entry => ({
      ...entry,
      title: entry.title[i18n.locale],
      description: entry.description[i18n.locale],
      team1: entry.team1[i18n.locale],
      team2: entry.team2[i18n.locale],
      widgets: entry.widgets.map(localizeWidget)
    })

    return currentDetails ? localizeDetails(currentDetails) : {}
  }
}

const mutations = {
  [SET_PROJECTS] (state, projects) {
    const createProject = (project = {}) => ({
      id: +project.id,
      title: {
        pl: nbsp(project.name || project.name_en),
        en: nbsp(project.name_en || project.name)
      },
      slug: {
        pl: project.slug || '',
        en: project.slug_en || ''
      },
      isPromoted: project.is_promoted,
      categories: project.categories.map(category => +category),
      photo: project.imgUrl || project.imgUrl_2x
    })
    const mapProjects = projects => projects.map(createProject)
    state.items = mapProjects(projects)
  },
  [SET_PROJECT_DETAILS] (state, details) {
    const getWidgetType = type => (
      type === 'photo' || type === 'two_photos' ? 'photo' : 'text'
    )

    const createWidget = (widget = {}) => ({
      type: getWidgetType(widget.side),
      title: {
        pl: nbsp(widget.title),
        en: nbsp(widget.title_en)
      },
      description: {
        pl: nbsp(widget.description),
        en: nbsp(widget.description_en)
      },
      photoDesc: {
        pl: nbsp(widget.photo_desc),
        en: nbsp(widget.photo_desc_en)
      },
      halfPhoto: !!(+widget.half_photo),
      photos: [widget.desktop_photo || widget.desktop_photo_2x, widget.photo2 || widget.photo2_2x]
    })

    const createDetails = (details = {}) => ({
      id: +details.id,
      title: {
        pl: nbsp(details.name),
        en: nbsp(details.name_en)
      },
      team1: {
        pl: details.team1_pl,
        en: details.team1_en
      },
      team2: {
        pl: nbsp(details.team2_pl),
        en: nbsp(details.team2_en)
      },
      description: {
        pl: nbsp(details.description),
        en: nbsp(details.description_en)
      },
      widgets: details.widgets.map(createWidget)
    })

    state.details = [...state.details, createDetails(details)]
  },
  [SET_CURRENT_PAGE] (state, id) {
    state.currentPage = id
  },
  [SET_CURRENT_PROJECT] (state, id) {
    state.currentProject = id
  },
  [SET_FILTERS] (state, filters = []) {
    const createFilter = (pl, en, id) => ({
      pl: pl || en,
      en: en || pl,
      id: +id
    })
    const mapFilters = filters => filters.map(filter => (
      createFilter(filter.name, filter.name_en, filter.id)
    ))

    state.filters = [
      ...mapFilters(filters),
      createFilter('wszystkie', 'all', -1)
    ]
  },
  [SET_ACTIVE_FILTER] (state, id = -1) {
    state.currentPage = 0
    state.activeFilter = +id
  },
  [TOGGLE_LOADING] (state, name) {
    state.loading = state.loading.includes(name)
      ? state.loading.filter(loadingName => loadingName !== name)
      : [...state.loading, name]
  }
}

const actions = {
  fetchFilters ({ commit, state, getters }) {
    if (!isEmpty(state.filters)) return Promise.resolve()

    return service.projects.filters().then((res = {}) => {
      commit(SET_FILTERS, res.categories)
    })
  },
  async fetchProjects ({ state, commit, dispatch, getters }) {
    const PROJECTS_LOADING = 'PROJECTS_LOADING'

    if (!isEmpty(state.items) || getters.isLoading(PROJECTS_LOADING)) return

    commit(TOGGLE_LOADING, PROJECTS_LOADING)
    if (!isEmpty(state.filters)) await dispatch('fetchFilters')

    service.projects.list().then((res = {}) => {
      commit(SET_PROJECTS, res.works)
      commit(TOGGLE_LOADING, PROJECTS_LOADING)
    })
  },
  async fetchProject ({ state, commit, getters, dispatch }, id) {
    const PROJECT_LOADING = 'PROJECT_LOADING'

    const isAlreadyFetched = state.details.find(entry => entry.id === id)
    if (getters.isLoading(PROJECT_LOADING)) return
    if (isAlreadyFetched) return Promise.resolve()

    if (isEmpty(state.items)) await dispatch('fetchProjects')

    commit(TOGGLE_LOADING, PROJECT_LOADING)

    try {
      const { portfolio = {} } = await service.projects.details(id)
      commit(SET_PROJECT_DETAILS, portfolio)
      commit(TOGGLE_LOADING, PROJECT_LOADING)
      return Promise.resolve()
    } catch (err) {
      commit(TOGGLE_LOADING, PROJECT_LOADING)
      return Promise.reject(err)
    }
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/projects.js